import {
  AlertOctagon,
  AlertTriangle,
  BadgeCheck,
  CircleHelp,
  Clock3,
  ShieldQuestion,
} from "lucide-react";
import {
  workspaceExpirationDays,
  type WorkspaceExpirationFilter,
} from "@api/shared-runtime/workspace-expiration";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/features/credentials/lib/format-date";
import type { WorkspaceProvider } from "../workspace-types";

const STATUS_PRESENTATION = {
  verified: {
    label: "Verified",
    icon: BadgeCheck,
    className: "border-emerald-200 bg-emerald-50 text-emerald-800",
  },
  "needs-review": {
    label: "Needs review",
    icon: Clock3,
    className: "border-amber-200 bg-amber-50 text-amber-800",
  },
  unverified: {
    label: "Unverified",
    icon: ShieldQuestion,
    className: "border-amber-200 bg-amber-50 text-amber-800",
  },
  expired: {
    label: "Expired",
    icon: AlertOctagon,
    className: "border-red-200 bg-red-50 text-red-800",
  },
  missing: {
    label: "Missing",
    icon: CircleHelp,
    className: "border-rose-200 bg-rose-50 text-rose-800",
  },
} as const;

/** The credentials column: the record's status, anything missing, and the
 * expiration picture. */
export function CredentialsCell({
  provider,
  expiration,
  showVerification,
}: {
  provider: WorkspaceProvider;
  expiration?: WorkspaceExpirationFilter;
  showVerification: boolean;
}) {
  const missingRequired =
    showVerification && provider.credentialStatus === "missing";
  return (
    <div className="flex min-w-40 flex-col items-start gap-1.5">
      {!showVerification && <CredentialBadge provider={provider} />}
      {missingRequired && (
        <Badge
          variant="outline"
          size="small"
          title={provider.credentialReason ?? undefined}
          className="gap-1 whitespace-nowrap border-rose-200 bg-rose-50 text-rose-800"
        >
          <CircleHelp className="size-3.5 shrink-0" aria-hidden="true" />
          Missing required
        </Badge>
      )}
      {provider.capabilities.canViewCredentials && (
        <CredentialExpiration provider={provider} expiration={expiration} />
      )}
    </div>
  );
}

function CredentialBadge({ provider }: { provider: WorkspaceProvider }) {
  const status = provider.credentialStatus;
  if (status === "expired" && provider.credentialExpiration?.expiredCount) {
    return null;
  }
  if (status === "none" || status === "unavailable") {
    return (
      <span
        title={provider.credentialReason ?? undefined}
        className="text-muted-foreground"
      >
        —
      </span>
    );
  }
  const presentation = STATUS_PRESENTATION[status];
  const Icon = presentation.icon;
  return (
    <Badge
      variant="outline"
      title={provider.credentialReason ?? undefined}
      className={`gap-1 whitespace-nowrap ${presentation.className}`}
    >
      <Icon className="size-3.5 shrink-0" aria-hidden="true" />
      {presentation.label}
    </Badge>
  );
}

function CredentialExpiration({
  provider,
  expiration,
}: {
  provider: WorkspaceProvider;
  expiration?: WorkspaceExpirationFilter;
}) {
  const summary = provider.credentialExpiration;
  if (!summary) {
    return null;
  }
  return (
    <>
      {summary.expiredCount > 0 && (
        <Badge
          variant="outline"
          size="small"
          className="gap-1 whitespace-nowrap border-red-300 bg-red-50 text-red-700"
        >
          <AlertOctagon className="size-3.5 shrink-0" aria-hidden="true" />
          {summary.expiredCount} expired
        </Badge>
      )}
      {summary.expiringCount > 0 && (
        <Badge
          variant="outline"
          size="small"
          className="gap-1 whitespace-nowrap border-amber-300 bg-amber-50 text-amber-700"
          title={`Credentials expiring within ${workspaceExpirationDays(expiration)} days, including today`}
        >
          <AlertTriangle className="size-3.5 shrink-0" aria-hidden="true" />
          {summary.expiringCount} expiring
        </Badge>
      )}
      <span className="whitespace-nowrap text-xs text-muted-foreground">
        {summary.nextExpiresAt ? (
          <>
            Next expiry{" "}
            <time dateTime={summary.nextExpiresAt}>
              {formatDate(summary.nextExpiresAt)}
            </time>
          </>
        ) : (
          "No upcoming expiry"
        )}
      </span>
    </>
  );
}
